import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { ITodo } from '@clickersphere/core/entities/ITodo';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { SupportedLanguageEnum } from '@clickersphere/core/enums/SupportedLanguageEnum';
import { useEvents } from '../../../../app/hooks/event/useEvents';
import { useLeads } from '../../../../app/hooks/lead/useLeads';
import { useCreateTodo } from '../../../../app/hooks/todo/useCreateTodo';
import { useUpdateTodo } from '../../../../app/hooks/todo/useUpdateTodo';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import {
  EN_US_DATE_FORMAT,
  parseDate,
  PT_BR_DATE_FORMAT,
} from '@clickersphere/core/utils/parseDate';
import useLanguageSelectorController from '../../LanguageSelector/useLanguageSelectorController';

interface IUseRegisterTodoForm {
  todo?: ITodo;
  presetLeadId?: string;
  presetEventId?: string;
}

export type AssociationType = 'none' | 'lead' | 'event';

const buildSchema = (dateFormat: string) =>
  z
    .object({
      title: z
        .string()
        .nonempty(t('todo.form.errors.title.required'))
        .max(200, t('todo.form.errors.title.maxLength')),
      description: z
        .string()
        .max(500, t('todo.form.errors.description.maxLength'))
        .transform((v) => (v === '' ? undefined : v))
        .optional(),
      dueDate: z
        .string()
        .optional()
        .refine(
          (value) => {
            if (!value || value.trim() === '') return true;
            return !!parseDate(value, dateFormat);
          },
          { message: t('todo.form.errors.dueDate.invalid') },
        ),
      associationType: z.enum(['none', 'lead', 'event']),
      leadId: z.string().optional(),
      eventId: z.string().optional(),
    })
    .refine((d) => !(d.leadId && d.eventId), {
      message: t('todo.form.errors.xor'),
      path: ['associationType'],
    });

export type TodoFormData = z.infer<ReturnType<typeof buildSchema>>;

export function useRegisterTodoFormController({
  todo,
  presetLeadId,
  presetEventId,
}: IUseRegisterTodoForm) {
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { toast } = useToast();
  const { handleError } = useError();
  const { createTodoAsync, createTodoIsPending } = useCreateTodo();
  const { updateTodoAsync, updateTodoIsPending } = useUpdateTodo();
  const { leads } = useLeads();
  const { events } = useEvents();

  const { selectedLanguage } = useLanguageSelectorController();
  const dateFormat =
    selectedLanguage === SupportedLanguageEnum['PT-BR']
      ? PT_BR_DATE_FORMAT
      : EN_US_DATE_FORMAT;

  const schema = buildSchema(dateFormat);

  const outputSchema = schema.transform((data) => ({
    ...data,
    dueDate: data.dueDate
      ? parseDate(data.dueDate, dateFormat)?.toISOString()
      : undefined,
  }));

  const defaultAssociationType: AssociationType =
    todo?.leadId || presetLeadId
      ? 'lead'
      : todo?.eventId || presetEventId
        ? 'event'
        : 'none';

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm<TodoFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: todo?.title ?? '',
      description: todo?.description ?? '',
      dueDate: todo?.dueDate ? format(new Date(todo.dueDate), dateFormat) : '',
      associationType: defaultAssociationType,
      leadId: todo?.leadId ?? presetLeadId ?? '',
      eventId: todo?.eventId ?? presetEventId ?? '',
    },
  });

  const associationType = watch('associationType');
  const currentLeadId = watch('leadId');

  const leadOptions = (leads ?? [])
    .filter((l) => l.active || l.id === currentLeadId)
    .map((l) => ({ value: l.id, text: l.name }));
  const eventOptions = (events ?? []).map((e) => ({
    value: e.id,
    text: e.description,
  }));

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    try {
      const processedData = outputSchema.parse(data);

      const resolvedLeadId =
        processedData.associationType === 'lead'
          ? processedData.leadId || undefined
          : null;
      const resolvedEventId =
        processedData.associationType === 'event'
          ? processedData.eventId || undefined
          : null;

      if (!todo) {
        await createTodoAsync({
          title: processedData.title,
          description: processedData.description,
          dueDate: processedData.dueDate,
          leadId: resolvedLeadId ?? undefined,
          eventId: resolvedEventId ?? undefined,
        });
        toast(t('todo.toast.created'), 'success');
      } else {
        await updateTodoAsync({
          id: todo.id,
          data: {
            title: processedData.title,
            description: processedData.description,
            dueDate: processedData.dueDate,
            leadId: resolvedLeadId,
            eventId: resolvedEventId,
          },
        });
        toast(t('todo.toast.updated'), 'success');
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('todo.toast.error'), 'error');
      }
    }
  });

  const handleAssociationTypeChange = (type: AssociationType) => {
    setValue('associationType', type);
    if (type !== 'lead') setValue('leadId', '');
    if (type !== 'event') setValue('eventId', '');
  };

  return {
    isFetching: createTodoIsPending || updateTodoIsPending,
    errors,
    control,
    handleSubmit,
    register,
    handleFormReset: reset,
    associationType,
    watch,
    leadOptions,
    eventOptions,
    handleAssociationTypeChange,
  };
}
